/**
 * TrustGate ECS Data Service
 * 
 * ECS-aware wrapper around TrustGateDataService.
 * Fetches raw Firestore/API data and adapts to canonical ECS types.
 * 
 * READ-ONLY: No schema changes, adapters only
 */

import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/init';
import type { Unsubscribe } from 'firebase/firestore';
import {
  fetchEpochMetadata,
  fetchRequestById as fetchRawRequestById,
  subscribeToRequests,
} from '../data/TrustGateDataService';
import {
  adaptRequestToEvent,
  adaptSystemEpochToECS,
  sortEventsByTimestamp,
} from './adapters';
import type {
  TrustGateEvent,
  TrustGateEpoch,
  TrustGateMetricsECS,
  TrustGateActivityStream,
} from './types';
import type { TrustGateRequest } from '../types';

/**
 * Fetch epoch metadata in ECS format
 */
export async function fetchEpochECS(): Promise<TrustGateEpoch> {
  const rawEpoch = await fetchEpochMetadata();
  return adaptSystemEpochToECS(rawEpoch);
}

/**
 * Fetch activity stream as ECS events
 */
export async function fetchActivityStreamECS(maxEvents = 10): Promise<TrustGateActivityStream> {
  try {
    // Query Firestore for recent requests
    const requestsQuery = query(
      collection(db, 'trustgate_requests'),
      orderBy('createdAt', 'desc'),
      limit(maxEvents)
    );
    
    const snapshot = await getDocs(requestsQuery);
    
    // Convert to ECS events
    const events: TrustGateEvent[] = snapshot.docs
      .map(doc => doc.data() as TrustGateRequest)
      .map(request => adaptRequestToEvent(request));
    
    // Sort by timestamp (newest first)
    const sortedEvents = sortEventsByTimestamp(events);
    
    return {
      events: sortedEvents,
      totalCount: sortedEvents.length,
      hasMore: sortedEvents.length === maxEvents,
      loading: false,
      error: null,
    };
  } catch (error) {
    console.error('[ECS DataService] Failed to fetch activity stream:', error);
    return {
      events: [],
      totalCount: 0,
      hasMore: false,
      loading: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Fetch metrics in ECS format
 */
export async function fetchMetricsECS(): Promise<TrustGateMetricsECS> {
  try {
    // Fetch all requests
    const requestsQuery = query(collection(db, 'trustgate_requests'));
    const snapshot = await getDocs(requestsQuery);
    
    // Convert to ECS events
    const events: TrustGateEvent[] = snapshot.docs
      .map(doc => doc.data() as TrustGateRequest)
      .map(request => adaptRequestToEvent(request));
    
    // Calculate today's start (UTC)
    const startOfToday = new Date();
    startOfToday.setUTCHours(0, 0, 0, 0);
    
    // Filter events today
    const eventsToday = events.filter(event => {
      const eventDate = new Date(event.timestamp);
      return eventDate >= startOfToday;
    });
    
    // Count by type
    const eventsByType: Record<string, number> = {};
    events.forEach(event => {
      eventsByType[event.type] = (eventsByType[event.type] || 0) + 1;
    });
    
    // Count by status
    const eventsByStatus: Record<string, number> = {};
    events.forEach(event => {
      eventsByStatus[event.status] = (eventsByStatus[event.status] || 0) + 1;
    });
    
    // Calculate approval rate
    const approved = events.filter(e => e.decision === 'ALLOW').length;
    const denied = events.filter(e => e.decision === 'DENY').length;
    const totalDecisions = approved + denied;
    const approvalRate = totalDecisions > 0 ? Math.round((approved / totalDecisions) * 100) : 0;
    
    // Count pending
    const pendingCount = events.filter(e => e.status === 'PENDING').length;
    
    // Count executed today
    const executedToday = eventsToday.filter(e => e.type === 'execution_completed').length;
    
    return {
      eventsToday: eventsToday.length,
      eventsByType: eventsByType as Record<string, number>,
      eventsByStatus: eventsByStatus as Record<string, number>,
      approvalRate,
      pendingCount,
      executedToday,
      loading: false,
      error: null,
    };
  } catch (error) {
    console.error('[ECS DataService] Failed to fetch metrics:', error);
    return {
      eventsToday: 0,
      eventsByType: {},
      eventsByStatus: {},
      approvalRate: 0,
      pendingCount: 0,
      executedToday: 0,
      loading: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Fetch single event by ID (request or proof)
 */
export async function fetchEventByIdECS(eventId: string): Promise<TrustGateEvent | null> {
  try {
    // Try as request ID first
    const request = await fetchRawRequestById(eventId);
    if (request) {
      return adaptRequestToEvent(request);
    }
    
    // TODO: Add proof lookup if needed
    return null;
  } catch (error) {
    console.error('[ECS DataService] Failed to fetch event:', error);
    return null;
  }
}

/**
 * Search events by criteria
 */
export async function searchEventsECS(criteria: {
  types?: string[];
  statuses?: string[];
  dateFrom?: Date;
  dateTo?: Date;
  limit?: number;
}): Promise<TrustGateEvent[]> {
  try {
    // Fetch all requests (in production, add query filters)
    const requestsQuery = query(
      collection(db, 'trustgate_requests'),
      orderBy('createdAt', 'desc'),
      limit(criteria.limit || 100)
    );
    
    const snapshot = await getDocs(requestsQuery);
    
    // Convert to ECS events
    let events: TrustGateEvent[] = snapshot.docs
      .map(doc => doc.data() as TrustGateRequest)
      .map(request => adaptRequestToEvent(request));
    
    // Apply filters
    if (criteria.types && criteria.types.length > 0) {
      events = events.filter(e => criteria.types!.includes(e.type));
    }
    
    if (criteria.statuses && criteria.statuses.length > 0) {
      events = events.filter(e => criteria.statuses!.includes(e.status));
    }
    
    if (criteria.dateFrom) {
      events = events.filter(e => new Date(e.timestamp) >= criteria.dateFrom!);
    }
    
    if (criteria.dateTo) {
      events = events.filter(e => new Date(e.timestamp) <= criteria.dateTo!);
    }
    
    return sortEventsByTimestamp(events);
  } catch (error) {
    console.error('[ECS DataService] Failed to search events:', error);
    return [];
  }
}

// ============================================================
// REAL-TIME LISTENERS (V2)
// ============================================================

/**
 * Subscribe to real-time activity stream updates
 * 
 * @param callback - Called with updated event array on each change
 * @param maxEvents - Maximum number of events to fetch (default: 20)
 * @returns Unsubscribe function to stop listening
 * 
 * MEMORY SAFETY:
 * - Caller MUST call unsubscribe function on component unmount
 * - Debounce updates in callback to prevent excessive re-renders
 * - Listener automatically cleaned up when unsubscribe() is called
 */
export function subscribeToActivityStreamECS(
  callback: (stream: TrustGateActivityStream) => void,
  maxEvents = 20
): Unsubscribe {
  // Subscribe to requests collection
  const unsubscribe = subscribeToRequests(
    (requests) => {
      try {
        // Convert to ECS events
        const events = requests.map(request => adaptRequestToEvent(request));
        
        // Sort by timestamp (newest first)
        const sortedEvents = sortEventsByTimestamp(events);
        
        // Build activity stream
        const stream: TrustGateActivityStream = {
          events: sortedEvents,
          totalCount: sortedEvents.length,
          hasMore: sortedEvents.length === maxEvents,
          loading: false,
          error: null,
        };
        
        callback(stream);
      } catch (error) {
        console.error('[ECS DataService] Failed to adapt activity stream:', error);
        callback({
          events: [],
          totalCount: 0,
          hasMore: false,
          loading: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    },
    maxEvents
  );

  return unsubscribe;
}

/**
 * Subscribe to real-time metrics updates
 * 
 * @param callback - Called with updated metrics on each change
 * @returns Unsubscribe function to stop listening
 * 
 * MEMORY SAFETY:
 * - Caller MUST call unsubscribe function on component unmount
 * - Consider debouncing callback to prevent excessive calculations
 * - Metrics calculation may be CPU-intensive for large datasets
 */
export function subscribeToMetricsECS(
  callback: (metrics: TrustGateMetricsECS) => void
): Unsubscribe {
  // Subscribe to requests collection (no limit for metrics calculation)
  const unsubscribe = subscribeToRequests(
    (requests) => {
      try {
        // Convert to ECS events
        const events = requests.map(request => adaptRequestToEvent(request));
        
        // Calculate today's start (UTC)
        const startOfToday = new Date();
        startOfToday.setUTCHours(0, 0, 0, 0);
        
        // Filter events today
        const eventsToday = events.filter(event => {
          const eventDate = new Date(event.timestamp);
          return eventDate >= startOfToday;
        });
        
        // Count by type
        const eventsByType: Record<string, number> = {};
        events.forEach(event => {
          eventsByType[event.type] = (eventsByType[event.type] || 0) + 1;
        });
        
        // Count by status
        const eventsByStatus: Record<string, number> = {};
        events.forEach(event => {
          eventsByStatus[event.status] = (eventsByStatus[event.status] || 0) + 1;
        });
        
        // Calculate approval rate
        const approved = events.filter(e => e.decision === 'ALLOW').length;
        const denied = events.filter(e => e.decision === 'DENY').length;
        const totalDecisions = approved + denied;
        const approvalRate = totalDecisions > 0 ? Math.round((approved / totalDecisions) * 100) : 0;
        
        // Count pending
        const pendingCount = events.filter(e => e.status === 'PENDING').length;
        
        // Count executed today
        const executedToday = eventsToday.filter(e => e.type === 'execution_completed').length;
        
        const metrics: TrustGateMetricsECS = {
          eventsToday: eventsToday.length,
          eventsByType: eventsByType as Record<string, number>,
          eventsByStatus: eventsByStatus as Record<string, number>,
          approvalRate,
          pendingCount,
          executedToday,
          loading: false,
          error: null,
        };
        
        callback(metrics);
      } catch (error) {
        console.error('[ECS DataService] Failed to calculate metrics:', error);
        callback({
          eventsToday: 0,
          eventsByType: {} as Record<string, number>,
          eventsByStatus: {} as Record<string, number>,
          approvalRate: 0,
          pendingCount: 0,
          executedToday: 0,
          loading: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    },
    100 // Fetch more items for accurate metrics
  );

  return unsubscribe;
}
